import { useTranslation } from 'react-i18next';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { formatMoney } from '@/shared/utils/money';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import type { AdminPaymentItem } from '../types/adminPayment';
import { formatDate, formatProviderReference } from '../utils/paymentHelpers';
import { PaymentProviderBadge } from './PaymentProviderBadge';
import { PaymentStatusBadge } from './PaymentStatusBadge';

interface PaymentMobileCardProps {
  payment: AdminPaymentItem;
  locale: string;
  onView: () => void;
}

export function PaymentMobileCard({ payment, locale, onView }: PaymentMobileCardProps) {
  const { t } = useTranslation();
  const unavailable = t('payments.identity.unavailable');
  const providerRef = formatProviderReference(payment);

  return (
    <article className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <TrialTruncatedText
            value={payment.customer_name ?? payment.tenant_name}
            fallback={unavailable}
            maxWidthClass="max-w-[14rem]"
            className="font-semibold text-foreground"
          />
          <TrialTruncatedText
            value={payment.product_name}
            fallback={unavailable}
            maxWidthClass="max-w-[12rem]"
            className="text-xs text-muted-foreground"
          />
        </div>
        <PaymentStatusBadge status={payment.status} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <PaymentProviderBadge provider={payment.provider} label={payment.provider_label} />
        <SubscriptionStatusBadge status={payment.subscription_status} />
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <div>
          <dt className="text-muted-foreground">{t('payments.amount')}</dt>
          <dd className="font-mono font-medium tabular-nums">
            {formatMoney(payment.amount, payment.currency)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('payments.date')}</dt>
          <dd className="tabular-nums">{formatDate(payment.created_at, locale)}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">{t('payments.columns.providerReference')}</dt>
          <dd className="font-mono truncate">{providerRef ?? unavailable}</dd>
        </div>
      </dl>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onView}>
          <Icons.Eye className="h-3.5 w-3.5" />
          {t('common.view')}
        </Button>
      </div>
    </article>
  );
}
